import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiJarExpense, ApiJarContribution, ApiJarPlanningItem, ApiJarCategory, ApiAccount } from "../../services/api";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Alert,
  Avatar,
  AvatarGroup,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useAuth } from "../../contexts/AuthContext";
import { SplitEditor } from "./SplitEditor";

type JarItem = {
  id: number;
  name: string;
  amount: number;
};

export type Jar = {
  id: string;
  name: string;
  description: string | null;
  targetAmountCents: number;
  currentAmountCents: number;
  targetDate?: string | null;
  currency: string;
  icon: string;
  color: string;
  ownerId: string;
  expenseCount?: number;
  totalExpenseCents?: number;
  updatedAt?: string;
  owner: { id: string; email: string | null; name: string | null };
  members: Array<{
    jarId: string;
    userId: string;
    role: string;
    user: { id: string; email: string | null; name: string | null };
  }>;
  items?: JarItem[];
};

interface JarDetailsDialogProps {
  open: boolean;
  jar: Jar | null;
  onClose: () => void;
  onAddMoney?: (jar: Jar) => void;
  onJarUpdated?: (updated: Jar) => void;
  initialTab?: number;
}

export function JarDetailsDialog({
  open,
  jar,
  onClose,
  onAddMoney,
  onJarUpdated,
  initialTab,
}: JarDetailsDialogProps) {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab ?? 0);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [editedName, setEditedName] = useState(jar?.name || "");
  const [editedDescription, setEditedDescription] = useState(jar?.description || "");
  const [budgetAmount, setBudgetAmount] = useState("");
  const [budgetDueDate, setBudgetDueDate] = useState("");
  const [budgetMonthlySavings, setBudgetMonthlySavings] = useState("");
  const [isAddingExpense, setIsAddingExpense] = useState(false);
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [expensePaidBy, setExpensePaidBy] = useState("");
  const [splitOption, setSplitOption] = useState("Equally");
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set());
  const [splitAllocations, setSplitAllocations] = useState<Record<string, number>>({});
  const [splitPercentages, setSplitPercentages] = useState<Record<string, number>>({});
  const [splitShares, setSplitShares] = useState<Record<string, number>>({});
  const [adjustmentAmounts, setAdjustmentAmounts] = useState<Record<string, number>>({});
  const [expenses, setExpenses] = useState<ApiJarExpense[]>([]);
  const [contributions, setContributions] = useState<ApiJarContribution[]>([]);
  const [isAddingContribution, setIsAddingContribution] = useState(false);
  const [newContributionAmount, setNewContributionAmount] = useState('');
  const [newContributionNote, setNewContributionNote] = useState('');
  const [expandedExpenseId, setExpandedExpenseId] = useState<string | null>(null);
  const [editSplitType, setEditSplitType] = useState("Equally");
  const [editSplitAmounts, setEditSplitAmounts] = useState<Record<string, number>>({});
  const [editSplitPercentages, setEditSplitPercentages] = useState<Record<string, number>>({});
  const [editSplitShares, setEditSplitShares] = useState<Record<string, number>>({});
  const [editAdjustmentAmounts, setEditAdjustmentAmounts] = useState<Record<string, number>>({});
  const [editSelectedParticipants, setEditSelectedParticipants] = useState<Set<string>>(new Set());
  const [editPaidBy, setEditPaidBy] = useState("");
  const [savingSplits, setSavingSplits] = useState(false);
  const participants = (jar?.members ?? []).map(m => ({
    id: m.userId,
    name: m.user.name ?? m.user.email ?? 'Unknown',
    email: m.user.email ?? '',
    role: m.role,
  }));
  const statusOptions = ["Planned", "Paid for", "Completed"] as const;
  const [planningItems, setPlanningItems] = useState<ApiJarPlanningItem[]>([]);
  // Member management state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"VIEWER" | "EDITOR">("VIEWER");
  const [inviting, setInviting] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [memberSuccess, setMemberSuccess] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null);

  // Accounts for funding source
  const [accounts, setAccounts] = useState<ApiAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // Jar categories
  const [jarCategories, setJarCategories] = useState<ApiJarCategory[]>([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [expenseCategoryId, setExpenseCategoryId] = useState<string | null>(null);

  // Bulk expense operations
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSplitDialogOpen, setBulkSplitDialogOpen] = useState(false);
  const [bulkSplitType, setBulkSplitType] = useState("equally");

  // Contribution editing
  const [editingContributionId, setEditingContributionId] = useState<string | null>(null);
  const [editContribAmount, setEditContribAmount] = useState("");
  const [editContribNote, setEditContribNote] = useState("");
  const [editContribDate, setEditContribDate] = useState("");
  const [editContribAccountId, setEditContribAccountId] = useState<string | null>(null);
  const [contributionDate, setContributionDate] = useState(new Date().toISOString().split('T')[0]);

  const isOwner = currentUser?.id === jar?.ownerId;

  const prevJarIdRef = useRef<string | null>(null);
  // Honour initialTab when it changes (e.g. Share menu)
  useEffect(() => {
    if (initialTab !== undefined) setActiveTab(initialTab);
  }, [initialTab, open]);

  useEffect(() => {
    if (!jar) return;

    const isNewJar = prevJarIdRef.current !== jar.id;
    prevJarIdRef.current = jar.id;

    // Always keep these in sync with the latest jar data
    setEditedName(jar.name);
    setEditedDescription(jar.description ?? "");

    // Only do the full reset when switching to a different jar,
    // not when the same jar updates from our own save
    if (isNewJar) {
      setActiveTab(initialTab ?? 0);
      setIsEditingHeader(false);
      setSelectedParticipants(new Set(jar.members.map(m => m.userId)));
      setExpensePaidBy("");
      setExpenses([]);
      setContributions([]);
      setPlanningItems([]);
      setIsAddingExpense(false);
      setIsAddingContribution(false);

      // Fetch expenses from the API
      api.getJarExpenses(jar.id)
        .then(({ expenses }) => setExpenses(expenses))
        .catch((err) => console.error('Failed to load jar expenses:', err));

      // Fetch contributions from the API
      api.getJarContributions(jar.id)
        .then(({ contributions }) => setContributions(contributions))
        .catch((err) => console.error('Failed to load jar contributions:', err));

      // Fetch planning items from the API
      api.getJarPlanningItems(jar.id)
        .then(({ items }) => setPlanningItems(items))
        .catch((err) => console.error('Failed to load planning items:', err));

      // Fetch jar categories
      api.getJarCategories(jar.id)
        .then(({ categories }) => setJarCategories(categories))
        .catch((err) => console.error('Failed to load jar categories:', err));

      // Fetch user accounts for funding source selection
      api.getAccounts('active')
        .then(({ accounts }) => setAccounts(accounts))
        .catch((err) => console.error('Failed to load accounts:', err));

      // Reset bulk state
      setBulkSelectedIds(new Set());
      setBulkMode(false);

      // Initialize budget fields from jar DB values
      if (jar.targetAmountCents > 0) {
        setBudgetAmount((jar.targetAmountCents / 100).toString());
        setBudgetDueDate(jar.targetDate ? jar.targetDate.split("T")[0] : "");
        // Auto-calc monthly savings if we have both target and date
        if (jar.targetDate) {
          const remaining = (jar.targetAmountCents - jar.currentAmountCents) / 100;
          const due = new Date(jar.targetDate);
          const now = new Date();
          const monthsLeft =
            (due.getFullYear() - now.getFullYear()) * 12 +
            (due.getMonth() - now.getMonth());
          setBudgetMonthlySavings(monthsLeft > 0 ? (remaining / monthsLeft).toFixed(2) : "");
        } else {
          setBudgetMonthlySavings("");
        }
      } else {
        setBudgetAmount("");
        setBudgetDueDate("");
        setBudgetMonthlySavings("");
      }
    }
  }, [jar]);

  // Save saving goal on blur (when user leaves an input field)
  const saveSavingGoal = useCallback(async () => {
    if (!jar) return;
    const targetCents = budgetAmount ? Math.round(parseFloat(budgetAmount) * 100) : 0;
    const targetDate = budgetDueDate || null;
    // Skip if nothing actually changed
    if (targetCents === jar.targetAmountCents && targetDate === (jar.targetDate ?? null)) return;
    try {
      await api.updateJar(jar.id, { targetAmountCents: targetCents, targetDate });
      onJarUpdated?.({ ...jar, targetAmountCents: targetCents, targetDate });
    } catch (err) {
      console.error('Failed to save saving goal:', err);
    }
  }, [jar, budgetAmount, budgetDueDate, onJarUpdated]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  const formatNumber = (value: number) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  const getItemTotal = (item: { amountCents: number; children?: { amountCents: number }[] }) => {
    const children = item.children ?? [];
    if (children.length === 0) return item.amountCents / 100;
    return children.reduce((sum, c) => sum + (c.amountCents ?? 0), 0) / 100;
  };

  const getPlanningTotal = () =>
    planningItems.reduce((sum, item) => sum + getItemTotal(item), 0);
  const formatExpenseDate = (value: string) => {
    const date = new Date(value);
    return {
      month: date.toLocaleString("en-US", { month: "short" }),
      day: date.getDate(),
    };
  };
  const splitOptions = ["Equally", "By exact amounts", "By percent", "By shares", "Adjustments"];
  const [editingAmountId, setEditingAmountId] = useState<string | null>(null);
  const [editingAmountValue, setEditingAmountValue] = useState<string>("");
  const [editingSubAmountKey, setEditingSubAmountKey] = useState<string | null>(null);
  const [editingSubAmountValue, setEditingSubAmountValue] = useState<string>("");

  const handleSaveExpense = async () => {
    if (!jar) return;
    const amount = Number(expenseAmount || 0);
    const amountCents = Math.round(amount * 100);

    // expensePaidBy is now a userId directly
    const paidByUserId = expensePaidBy;

    // Compute splits based on split option
    const activeParticipants = participants.filter(p => selectedParticipants.has(p.id));
    let splits: Array<{ userId: string; amountCents: number }> = [];

    if (splitOption === 'Equally') {
      const perPerson = Math.floor(amountCents / activeParticipants.length);
      const remainder = amountCents - (perPerson * activeParticipants.length);
      splits = activeParticipants.map((p, i) => ({
        userId: p.id,
        amountCents: perPerson + (i < remainder ? 1 : 0),
      }));
    } else if (splitOption === 'By exact amounts') {
      splits = participants
        .filter(p => (splitAllocations[p.id] ?? 0) > 0)
        .map(p => ({
          userId: p.id,
          amountCents: Math.round((splitAllocations[p.id] ?? 0) * 100),
        }));
    } else if (splitOption === 'By percent') {
      splits = participants
        .filter(p => (splitPercentages[p.id] ?? 0) > 0)
        .map(p => ({
          userId: p.id,
          amountCents: Math.round(amountCents * (splitPercentages[p.id] ?? 0) / 100),
        }));
    } else if (splitOption === 'By shares') {
      const sharesParticipants = activeParticipants.filter(p => (splitShares[p.id] ?? 1) > 0);
      const totalShares = sharesParticipants.reduce((sum, p) => sum + (splitShares[p.id] ?? 1), 0);
      let allocated = 0;
      splits = sharesParticipants.map((p, i) => {
        const raw = amountCents * (splitShares[p.id] ?? 1) / (totalShares || 1);
        const cents = i < sharesParticipants.length - 1 ? Math.floor(raw) : amountCents - allocated;
        allocated += cents;
        return { userId: p.id, amountCents: cents };
      });
    } else if (splitOption === 'Adjustments') {
      const selectedIds = activeParticipants.map(p => p.id);
      const baseSplit = Math.floor(amountCents / selectedIds.length);
      const adjustedIds = selectedIds.filter(id => (adjustmentAmounts[id] ?? 0) !== 0);
      const totalAdjCents = adjustedIds.reduce((sum, id) => sum + Math.round((adjustmentAmounts[id] ?? 0) * 100), 0);
      const remainingCount = selectedIds.length - adjustedIds.length;
      const offsetCents = remainingCount > 0 ? Math.round(totalAdjCents / remainingCount) : 0;
      splits = activeParticipants.map(p => {
        const adjCents = Math.round((adjustmentAmounts[p.id] ?? 0) * 100);
        return {
          userId: p.id,
          amountCents: adjustedIds.includes(p.id) ? baseSplit + adjCents : baseSplit - offsetCents,
        };
      });
    }

    // Map split option to DB enum
    const splitTypeMap: Record<string, string> = {
      'Equally': 'equally',
      'By exact amounts': 'exact',
      'By percent': 'percent',
      'By shares': 'shares',
      'Adjustments': 'adjustments',
    };

    try {
      const { expense } = await api.createJarExpense(jar.id, {
        description: expenseDescription || 'New expense',
        amountCents,
        currency: jar.currency,
        occurredAt: new Date(expenseDate).toISOString(),
        paidByUserId,
        categoryId: expenseCategoryId || undefined,
        splitType: splitTypeMap[splitOption] ?? 'equally',
        splits,
      });
      setExpenses(prev => [expense, ...prev]);
    } catch (err) {
      console.error('Failed to save expense:', err);
    }

    setExpenseDescription("");
    setExpenseAmount("");
    setExpenseDate(new Date().toISOString().split("T")[0]);
    setExpensePaidBy("");
    setExpenseCategoryId(null);
    setSplitOption("Equally");
    setSelectedParticipants(new Set(participants.map(p => p.id)));
    setSplitAllocations({});
    setSplitPercentages({});
    setSplitShares({});
    setAdjustmentAmounts({});
    setIsAddingExpense(false);
  };

  const handleCancelAddExpense = () => {
    setIsAddingExpense(false);
    setSelectedParticipants(new Set(participants.map(p => p.id)));
    setSplitAllocations({});
    setSplitPercentages({});
    setSplitShares({});
    setAdjustmentAmounts({});
  };

  // Map DB split type back to UI label
  const splitTypeToLabel: Record<string, string> = {
    'equally': 'Equally',
    'exact': 'By exact amounts',
    'percent': 'By percent',
    'shares': 'By shares',
    'adjustments': 'Adjustments',
  };
  const labelToSplitType: Record<string, string> = {
    'Equally': 'equally',
    'By exact amounts': 'exact',
    'By percent': 'percent',
    'By shares': 'shares',
    'Adjustments': 'adjustments',
  };

  const handleExpandExpense = (expense: ApiJarExpense) => {
    if (expandedExpenseId === expense.id) {
      setExpandedExpenseId(null);
      return;
    }
    setExpandedExpenseId(expense.id);
    const type = splitTypeToLabel[expense.splitType] ?? 'Equally';
    setEditSplitType(type);
    setEditPaidBy(expense.paidByUserId);

    // Exact-dollar amounts
    const amounts: Record<string, number> = {};
    for (const split of expense.splits) {
      amounts[split.userId] = split.amountCents / 100;
    }
    setEditSplitAmounts(amounts);

    // Percentages derived from split amounts
    const totalCents = expense.amountCents || 1;
    const percents: Record<string, number> = {};
    for (const split of expense.splits) {
      percents[split.userId] = Math.round((split.amountCents / totalCents) * 10000) / 100;
    }
    setEditSplitPercentages(percents);

    // Shares: default 1 per participant (originals can't be recovered from stored data)
    const shares: Record<string, number> = {};
    for (const p of participants) { shares[p.id] = 1; }
    setEditSplitShares(shares);

    // Adjustments: default to 0
    const adjs: Record<string, number> = {};
    for (const p of participants) { adjs[p.id] = 0; }
    setEditAdjustmentAmounts(adjs);

    // Selected participants = those with a non-zero split
    const splitUserIds = new Set(expense.splits.filter(s => s.amountCents > 0).map(s => s.userId));
    setEditSelectedParticipants(splitUserIds);
  };

  const handleSaveExpenseSplits = async (expense: ApiJarExpense) => {
    if (!jar) return;
    setSavingSplits(true);
    try {
      const paidByUserId = editPaidBy || expense.paidByUserId;
      const totalCents = expense.amountCents;
      const activeParticipants = participants.filter(p => editSelectedParticipants.has(p.id));
      let splits: Array<{ userId: string; amountCents: number }> = [];

      if (editSplitType === 'Equally') {
        const count = activeParticipants.length || 1;
        const perPerson = Math.floor(totalCents / count);
        const remainder = totalCents - perPerson * count;
        splits = activeParticipants.map((p, i) => ({
          userId: p.id,
          amountCents: perPerson + (i < remainder ? 1 : 0),
        }));
      } else if (editSplitType === 'By exact amounts') {
        splits = participants
          .filter(p => (editSplitAmounts[p.id] ?? 0) > 0)
          .map(p => ({
            userId: p.id,
            amountCents: Math.round((editSplitAmounts[p.id] ?? 0) * 100),
          }));
      } else if (editSplitType === 'By percent') {
        splits = participants
          .filter(p => (editSplitPercentages[p.id] ?? 0) > 0)
          .map(p => ({
            userId: p.id,
            amountCents: Math.round(totalCents * (editSplitPercentages[p.id] ?? 0) / 100),
          }));
      } else if (editSplitType === 'By shares') {
        const active = participants.filter(p => (editSplitShares[p.id] ?? 1) > 0);
        const totalShares = active.reduce((sum, p) => sum + (editSplitShares[p.id] ?? 1), 0);
        let allocated = 0;
        splits = active.map((p, i) => {
          const raw = totalCents * (editSplitShares[p.id] ?? 1) / (totalShares || 1);
          const cents = i < active.length - 1 ? Math.floor(raw) : totalCents - allocated;
          allocated += cents;
          return { userId: p.id, amountCents: cents };
        });
      } else if (editSplitType === 'Adjustments') {
        const selectedIds = activeParticipants.map(p => p.id);
        const baseSplit = Math.floor(totalCents / (selectedIds.length || 1));
        const adjustedIds = selectedIds.filter(id => (editAdjustmentAmounts[id] ?? 0) !== 0);
        const totalAdjCents = adjustedIds.reduce(
          (sum, id) => sum + Math.round((editAdjustmentAmounts[id] ?? 0) * 100), 0
        );
        const remainingCount = selectedIds.length - adjustedIds.length;
        const offsetCents = remainingCount > 0 ? Math.round(totalAdjCents / remainingCount) : 0;
        splits = activeParticipants.map(p => {
          const adjCents = Math.round((editAdjustmentAmounts[p.id] ?? 0) * 100);
          return {
            userId: p.id,
            amountCents: adjustedIds.includes(p.id) ? baseSplit + adjCents : baseSplit - offsetCents,
          };
        });
      }

      const { expense: updated } = await api.updateJarExpense(jar.id, expense.id, {
        splitType: labelToSplitType[editSplitType] ?? 'equally',
        paidByUserId,
        splits,
      });
      setExpenses(prev => prev.map(e => e.id === updated.id ? updated : e));
      setExpandedExpenseId(null);
    } catch (err) {
      console.error('Failed to update expense splits:', err);
    } finally {
      setSavingSplits(false);
    }
  };

  const handleEditSplitTypeChange = (value: string, expense: ApiJarExpense) => {
    setEditSplitType(value);
    const totalCents = expense.amountCents;
    if (value === 'Equally') {
      const count = participants.length || 1;
      const perPerson = Math.floor(totalCents / count);
      const remainder = totalCents - perPerson * count;
      const amounts: Record<string, number> = {};
      participants.forEach((p, i) => {
        amounts[p.id] = (perPerson + (i < remainder ? 1 : 0)) / 100;
      });
      setEditSplitAmounts(amounts);
      setEditSelectedParticipants(new Set(participants.map(p => p.id)));
    } else if (value === 'By percent') {
      // Derive percentages from current dollar amounts if possible
      const total = Object.values(editSplitAmounts).reduce((s, v) => s + v, 0);
      const percents: Record<string, number> = {};
      participants.forEach(p => {
        percents[p.id] = total > 0
          ? Math.round((editSplitAmounts[p.id] ?? 0) / total * 10000) / 100
          : 0;
      });
      setEditSplitPercentages(percents);
    } else if (value === 'By shares') {
      // Reset to 1 share each
      const shares: Record<string, number> = {};
      participants.forEach(p => { shares[p.id] = 1; });
      setEditSplitShares(shares);
    } else if (value === 'Adjustments') {
      setEditSelectedParticipants(new Set(participants.map(p => p.id)));
      const adjs: Record<string, number> = {};
      participants.forEach(p => { adjs[p.id] = 0; });
      setEditAdjustmentAmounts(adjs);
    }
    // For 'By exact amounts': editSplitAmounts already contains the right values
  };

  const handleSplitOptionChange = (value: string) => {
    setSplitOption(value);
    if (value === "Equally" || value === "Adjustments") {
      setSelectedParticipants(new Set(participants.map((p) => p.id)));
    }
  };

  const handleExpenseAmountChange = (value: string) => {
    if (value === "") {
      setExpenseAmount(value);
      return;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && numericValue >= 0) {
      setExpenseAmount(value);
    }
  };

  const handleExpenseAmountBlur = () => {
    if (expenseAmount === "") {
      return;
    }

    const numericValue = Number(expenseAmount);
    if (!Number.isNaN(numericValue)) {
      setExpenseAmount(numericValue.toFixed(2));
    }
  };

  const handleToggleParticipant = (participantId: string) => {
    setSelectedParticipants((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(participantId)) {
        newSet.delete(participantId);
      } else {
        newSet.add(participantId);
      }
      return newSet;
    });
  };

  const handleSelectAllParticipants = () => {
    setSelectedParticipants(new Set(participants.map((p) => p.id)));
  };

  const handleUpdateAllocation = (participantId: string, value: number) => {
    setSplitAllocations((prev) => ({ ...prev, [participantId]: value }));
  };

  const handleUpdatePercentage = (participantId: string, value: number) => {
    setSplitPercentages((prev) => ({ ...prev, [participantId]: value }));
  };

  const handleUpdateShares = (participantId: string, value: number) => {
    // Allow 0 value for shares
    if (value === 0 || !Number.isNaN(value)) {
      setSplitShares((prev) => ({ ...prev, [participantId]: value }));
    }
  };

  const handleUpdateAdjustment = (participantId: string, value: number) => {
    setAdjustmentAmounts((prev) => ({ ...prev, [participantId]: value }));
  };

  const handleAddItem = async () => {
    if (!jar) return;
    try {
      const { item } = await api.createJarPlanningItem(jar.id, {
        name: 'New Item',
        amountCents: 0,
        status: 'Planned',
      });
      setPlanningItems((prev) => [...prev, { ...item, children: item.children ?? [] }]);
    } catch (err) {
      console.error('Failed to add planning item:', err);
    }
  };

  const handleAddSubItem = async (itemId: string) => {
    if (!jar) return;
    try {
      const { item: subItem } = await api.createJarPlanningItem(jar.id, {
        parentId: itemId,
        name: 'New Sub-item',
        amountCents: 0,
        status: 'Planned',
      });
      setPlanningItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, children: [...(item.children ?? []), { ...subItem, children: subItem.children ?? [] }] }
            : item
        )
      );
    } catch (err) {
      console.error('Failed to add sub-item:', err);
    }
  };

  const updateItemField = (
    itemId: string,
    field: "name" | "amount" | "status",
    value: string
  ) => {
    if (field === 'amount') {
      const amountCents = Math.round(Number(value) * 100);
      setPlanningItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, amountCents } : item
        )
      );
      if (jar) {
        api.updateJarPlanningItem(jar.id, itemId, { amountCents }).catch(err =>
          console.error('Failed to update planning item:', err));
      }
    } else {
      setPlanningItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, [field]: value } : item
        )
      );
      if (jar) {
        api.updateJarPlanningItem(jar.id, itemId, { [field]: value }).catch(err =>
          console.error('Failed to update planning item:', err));
      }
    }
  };

  const updateSubItemField = (
    itemId: string,
    subItemId: string,
    field: "name" | "amount" | "status",
    value: string
  ) => {
    if (field === 'amount') {
      const amountCents = Math.round(Number(value) * 100);
      setPlanningItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? {
                ...item,
                children: (item.children ?? []).map((subItem) =>
                  subItem.id === subItemId ? { ...subItem, amountCents } : subItem
                ),
              }
            : item
        )
      );
      if (jar) {
        api.updateJarPlanningItem(jar.id, subItemId, { amountCents }).catch(err =>
          console.error('Failed to update planning sub-item:', err));
      }
    } else {
      setPlanningItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? {
                ...item,
                children: (item.children ?? []).map((subItem) =>
                  subItem.id === subItemId ? { ...subItem, [field]: value } : subItem
                ),
              }
            : item
        )
      );
      if (jar) {
        api.updateJarPlanningItem(jar.id, subItemId, { [field]: value }).catch(err =>
          console.error('Failed to update planning sub-item:', err));
      }
    }
  };

  const handleDeleteItem = (itemId: string) => {
    setPlanningItems((prev) => prev.filter((item) => item.id !== itemId));
    if (jar) {
      api.deleteJarPlanningItem(jar.id, itemId).catch(err =>
        console.error('Failed to delete planning item:', err));
    }
  };

  const handleDeleteSubItem = (itemId: string, subItemId: string) => {
    setPlanningItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              children: (item.children ?? []).filter(
                (subItem) => subItem.id !== subItemId
              ),
            }
          : item
      )
    );
    if (jar) {
      api.deleteJarPlanningItem(jar.id, subItemId).catch(err =>
        console.error('Failed to delete planning sub-item:', err));
    }
  };

  const handleBudgetAmountChange = (value: string) => {
    if (value !== "" && parseFloat(value) < 0) return;
    setBudgetAmount(value);
    const amount = parseFloat(value);
    if (!value || isNaN(amount) || amount <= 0) return;
    // If date is set, recompute monthly savings
    if (budgetDueDate) {
      const dueDate = new Date(budgetDueDate);
      const today = new Date();
      const monthsDiff =
        (dueDate.getFullYear() - today.getFullYear()) * 12 +
        (dueDate.getMonth() - today.getMonth());
      if (monthsDiff > 0) {
        setBudgetMonthlySavings((amount / monthsDiff).toFixed(2));
      }
    } else if (budgetMonthlySavings) {
      // If monthly savings is set, recompute the date
      const monthly = parseFloat(budgetMonthlySavings);
      if (monthly > 0) {
        const monthsNeeded = Math.ceil(amount / monthly);
        const dueDate = new Date();
        dueDate.setMonth(dueDate.getMonth() + monthsNeeded);
        setBudgetDueDate(dueDate.toISOString().split("T")[0]);
      }
    }
  };

  const handleBudgetDueDateChange = (value: string) => {
    setBudgetDueDate(value);
    // Auto-calculate monthly savings if budget amount exists
    if (budgetAmount && value) {
      const amount = parseFloat(budgetAmount);
      const dueDate = new Date(value);
      const today = new Date();
      const monthsDiff =
        (dueDate.getFullYear() - today.getFullYear()) * 12 +
        (dueDate.getMonth() - today.getMonth());
      if (monthsDiff > 0) {
        const monthlySavings = (amount / monthsDiff).toFixed(2);
        setBudgetMonthlySavings(monthlySavings);
      }
    }
  };

  const handleBudgetMonthlySavingsChange = (value: string) => {
    setBudgetMonthlySavings(value);
    // Auto-calculate due date if budget amount exists
    if (budgetAmount && value) {
      const amount = parseFloat(budgetAmount);
      const monthly = parseFloat(value);
      if (monthly > 0) {
        const monthsNeeded = Math.ceil(amount / monthly);
        const dueDate = new Date();
        dueDate.setMonth(dueDate.getMonth() + monthsNeeded);
        setBudgetDueDate(dueDate.toISOString().split("T")[0]);
      }
    }
  };

  // Validation helpers
  const getValidationError = () => {
    const amount = Number(expenseAmount);

    if (splitOption === "By percent") {
      const totalPercent = Object.values(splitPercentages).reduce((sum, val) => sum + val, 0);
      if (Math.abs(totalPercent - 100) > 0.01) {
        return `Percentages must equal 100% (currently ${totalPercent.toFixed(2)}%)`;
      }
    }

    if (splitOption === "By exact amounts") {
      const totalExact = Object.values(splitAllocations).reduce((sum, val) => sum + val, 0);
      if (Math.abs(totalExact - amount) > 0.01) {
        return `Amounts must equal ${formatCurrency(amount)} (currently ${formatCurrency(totalExact)})`;
      }
    }

    return null;
  };

  const validationError = getValidationError();

  /** Validate the edit-mode split inputs against the given expense amount in cents. */
  const getEditValidationError = (expenseAmountCents: number): string | null => {
    const amount = expenseAmountCents / 100;

    if (editSplitType === "By percent") {
      const totalPercent = Object.values(editSplitPercentages).reduce((sum, val) => sum + val, 0);
      if (Math.abs(totalPercent - 100) > 0.01) {
        return `Percentages must equal 100% (currently ${totalPercent.toFixed(2)}%)`;
      }
    }

    if (editSplitType === "By exact amounts") {
      const totalExact = Object.values(editSplitAmounts).reduce((sum, val) => sum + val, 0);
      if (Math.abs(totalExact - amount) > 0.01) {
        return `Amounts must equal ${formatCurrency(amount)} (currently ${formatCurrency(totalExact)})`;
      }
    }

    return null;
  };

  const handleSaveHeaderEdit = async () => {
    if (!jar) return;
    try {
      const { jar: updatedJar } = await api.updateJar(jar.id, {
        name: editedName,
        description: editedDescription || null,
      });
      onJarUpdated?.({
        ...jar,
        ...updatedJar,
      });
    } catch (err) {
      console.error('Failed to save jar header:', err);
    }
    setIsEditingHeader(false);
  };

  const handleCancelHeaderEdit = () => {
    setEditedName(jar?.name || "");
    setEditedDescription(jar?.description || "");
    setIsEditingHeader(false);
  };

  if (!jar) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ p: 2 }}>
        {isAddingExpense ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton onClick={handleCancelAddExpense}>
              <CloseIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={600}>
              Add an expense
            </Typography>
            <Button onClick={handleSaveExpense} color="success" disabled={!!validationError}>
              Add
            </Button>
          </Box>
        ) : isEditingHeader ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: jar.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "24px" }}>{jar.icon}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                fullWidth
                variant="standard"
                size="small"
                sx={{ mb: 1 }}
                inputProps={{ style: { fontSize: "1.25rem", fontWeight: 600 } }}
              />
              <TextField
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                fullWidth
                variant="standard"
                size="small"
                multiline
                maxRows={2}
                inputProps={{ style: { fontSize: "0.875rem" } }}
              />
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button size="small" color="success" onClick={handleSaveHeaderEdit}>
                  Save
                </Button>
                <Button size="small" onClick={handleCancelHeaderEdit}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              cursor: "pointer",
              "&:hover": { opacity: 0.7 },
            }}
            onClick={() => setIsEditingHeader(true)}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: jar.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "24px" }}>{jar.icon}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{jar.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {jar.description}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogTitle>
      <DialogContent
        sx={{ bgcolor: isAddingExpense ? "background.paper" : "grey.100" }}
      >
        {isAddingExpense ? (
          <Stack spacing={3} sx={{ mt: 1, maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}>

            <TextField
              label="Description"
              value={expenseDescription}
              onChange={(e) => setExpenseDescription(e.target.value)}
              fullWidth
              placeholder="Enter a description"
              required
              error={!expenseDescription.trim() && !!validationError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">🧾</InputAdornment>
                ),
              }}
            />

            <TextField
              label="Amount"
              value={expenseAmount}
              onChange={(e) => handleExpenseAmountChange(e.target.value)}
              onBlur={handleExpenseAmountBlur}
              type="number"
              fullWidth
              required
              error={(!expenseAmount || Number(expenseAmount) <= 0) && !!validationError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: "0.01" }}
            />

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Date"
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 200, flex: 1 }}
              />
              <FormControl sx={{ minWidth: 200, flex: 1 }}>
                <TextField
                  select
                  label="Paid by"
                  value={expensePaidBy}
                  onChange={(e) => setExpensePaidBy(e.target.value)}
                >
                  {participants.map((participant) => (
                    <MenuItem key={participant.id} value={participant.id}>
                      {participant.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Box>

            {/* Category selector */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <FormControl sx={{ flex: 1 }}>
                <TextField
                  select
                  label="Category (optional)"
                  value={expenseCategoryId ?? ""}
                  onChange={(e) => setExpenseCategoryId(e.target.value || null)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>No category</em>
                  </MenuItem>
                  {jarCategories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", pt: 0.5 }}>
                <TextField
                  placeholder="New category"
                  size="small"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  sx={{ width: 140 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!newCategoryName.trim()}
                  onClick={async () => {
                    if (!jar || !newCategoryName.trim()) return;
                    try {
                      const { category } = await api.createJarCategory(jar.id, { name: newCategoryName.trim() });
                      setJarCategories(prev => [...prev, category]);
                      setExpenseCategoryId(category.id);
                      setNewCategoryName("");
                    } catch (err) {
                      console.error('Failed to create category:', err);
                    }
                  }}
                >
                  +
                </Button>
              </Box>
            </Box>

            {/* Split + Participant Editor */}
            <SplitEditor
              participants={participants}
              totalAmount={Number(expenseAmount) || 0}
              splitType={splitOption}
              onSplitTypeChange={handleSplitOptionChange}
              selectedParticipants={selectedParticipants}
              onToggleParticipant={handleToggleParticipant}
              onSelectAll={handleSelectAllParticipants}
              splitAllocations={splitAllocations}
              onUpdateAllocation={handleUpdateAllocation}
              splitPercentages={splitPercentages}
              onUpdatePercentage={handleUpdatePercentage}
              splitShares={splitShares}
              onUpdateShares={handleUpdateShares}
              adjustmentAmounts={adjustmentAmounts}
              onUpdateAdjustment={handleUpdateAdjustment}
              validationError={validationError}
            />
          </Stack>
        ) : (
          <Stack spacing={3} sx={{ mt: 3 }}>
          {/* Saved Amount Section — only shown for jars with a savings goal */}
          {jar.targetAmountCents > 0 && (() => {
            const progressPct = Math.min((jar.currentAmountCents / jar.targetAmountCents) * 100, 100);
            const remainingCents = Math.max(jar.targetAmountCents - jar.currentAmountCents, 0);
            return (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Saving Goal
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {jar.currency} {(jar.currentAmountCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Goal: {jar.currency} {(jar.targetAmountCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    bgcolor: "grey.200",
                    "& .MuiLinearProgress-bar": { bgcolor: "grey.800", borderRadius: 1 },
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {progressPct.toFixed(1)}% complete
                  </Typography>
                  <Box sx={{ textAlign: "right" }}>
                    {jar.targetDate && (
                      <Typography variant="body2" color="text.secondary">
                        Target: {new Date(jar.targetDate).toLocaleDateString()}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Remaining: {jar.currency} {(remainingCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })()}

          {/* Budget Progress Section - REMOVED, consolidated into Saved Amount Section */}

          {/* Participants Section */}
          {jar.members.length > 1 && (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 4,
                p: 3,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="h6" sx={{ mb: 3 }}>
                Participants
              </Typography>
              <Stack spacing={2.5}>
                {participants.map((participant) => (
                  <Box
                    key={participant.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: "grey.200",
                          color: "text.primary",
                          width: 48,
                          height: 48,
                        }}
                      >
                        {participant.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {participant.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {participant.email}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={participant.role === 'OWNER' ? 'Owner' : participant.role === 'EDITOR' ? 'Editor' : 'Viewer'}
                      sx={{
                        px: 1.5,
                        fontWeight: 600,
                        bgcolor: participant.role === 'OWNER' ? "grey.900" : "grey.100",
                        color: participant.role === 'OWNER' ? "common.white" : "text.primary",
                      }}
                    />
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                  onClick={() => setActiveTab(3)}
                  sx={{
                    mt: 1,
                    borderRadius: 2,
                    py: 1.25,
                    borderColor: "grey.400",
                    color: "text.primary",
                  }}
                >
                  Add Participant
                </Button>
              </Stack>
            </Box>
          )}

          {/* Tabs Navigation */}
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 4,
              p: 3,
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                centered
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: 500,
                  },
                }}
              >
                <Tab label="Saving" />
                <Tab label="Planning" />
                <Tab label="Expenses" />
                <Tab label="Members" />
              </Tabs>
            </Box>

            {/* Saving Tab */}
            {activeTab === 0 && (
              <Box>
                <Stack spacing={4}>
                  {/* Saving Goal Section */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                      Saving Goal
                    </Typography>
                    <Stack spacing={3}>
                      <TextField
                        label="Target Amount ($)"
                        type="number"
                        value={budgetAmount}
                        onChange={(e) => handleBudgetAmountChange(e.target.value)}
                        onBlur={saveSavingGoal}
                        fullWidth
                        size="small"
                        placeholder="Leave empty for expense-only jars"
                        inputProps={{ step: "0.01", min: "0" }}
                      />
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: !budgetAmount ? "action.disabledBackground" : "transparent",
                          borderRadius: 1,
                          opacity: !budgetAmount ? 0.5 : 1,
                          pointerEvents: !budgetAmount ? "none" : "auto",
                        }}
                      >
                        <Stack spacing={2}>
                          <TextField
                            label="Target Date"
                            type="date"
                            value={budgetDueDate}
                            onChange={(e) => handleBudgetDueDateChange(e.target.value)}
                            onBlur={saveSavingGoal}
                            fullWidth
                            size="small"
                            disabled={!budgetAmount}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Monthly Savings Goal"
                            type="number"
                            value={budgetMonthlySavings}
                            onChange={(e) => handleBudgetMonthlySavingsChange(e.target.value)}
                            onBlur={saveSavingGoal}
                            fullWidth
                            size="small"
                            disabled={!budgetAmount}
                            inputProps={{ step: "0.01" }}
                            helperText="Set either this or a target date — the other updates automatically"
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Contributions Section */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                      sx={{ mb: 2 }}
                    >
                      Contributions
                    </Typography>
                    {/* Summary Box */}
                    <Box sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 1.5, mb: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Total Contributions
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {formatCurrency(contributions.reduce((s, c) => s + c.amountCents, 0) / 100)}
                        </Typography>
                      </Box>
                      <Stack spacing={0.75}>
                          {participants.map((participant) => {
                            const total = contributions
                              .filter(c => c.userId === participant.id)
                              .reduce((s, c) => s + c.amountCents, 0);
                            return (
                              <Box
                                key={participant.id}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  {participant.name}:
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {formatCurrency(total / 100)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                      {isAddingContribution ? (
                        <Box sx={{ mt: 1 }}>
                          <Stack spacing={1.5}>
                            <TextField
                              label="Amount ($)"
                              type="number"
                              size="small"
                              fullWidth
                              inputProps={{ step: '0.01', min: '0.01' }}
                              value={newContributionAmount}
                              onChange={(e) => setNewContributionAmount(e.target.value)}
                            />
                            <TextField
                              label="Date"
                              type="date"
                              size="small"
                              fullWidth
                              value={contributionDate}
                              onChange={(e) => setContributionDate(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                              label="Note (optional)"
                              size="small"
                              fullWidth
                              value={newContributionNote}
                              onChange={(e) => setNewContributionNote(e.target.value)}
                            />
                            {accounts.length > 0 && (
                              <TextField
                                select
                                label="Funding Source (account)"
                                size="small"
                                fullWidth
                                value={selectedAccountId ?? ""}
                                onChange={(e) => setSelectedAccountId(e.target.value || null)}
                                helperText="Which account is this money in?"
                              >
                                <MenuItem value="">
                                  <em>Not specified</em>
                                </MenuItem>
                                {accounts.map((acc) => (
                                  <MenuItem key={acc.id} value={acc.id}>
                                    {acc.name} ({acc.institution ?? acc.type})
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={!newContributionAmount || parseFloat(newContributionAmount) <= 0}
                                onClick={async () => {
                                  if (!jar) return;
                                  const dollars = parseFloat(newContributionAmount);
                                  if (!dollars || dollars <= 0) return;
                                  try {
                                    const { contribution, jarBalance } = await api.createJarContribution(jar.id, {
                                      amountCents: Math.round(dollars * 100),
                                      note: newContributionNote || undefined,
                                      occurredAt: contributionDate,
                                      accountId: selectedAccountId || undefined,
                                    });
                                    setContributions(prev => [contribution, ...prev]);
                                    onJarUpdated?.({ ...jar, currentAmountCents: jarBalance });
                                    setNewContributionAmount('');
                                    setNewContributionNote('');
                                    setSelectedAccountId(null);
                                    setContributionDate(new Date().toISOString().split('T')[0]);
                                    setIsAddingContribution(false);
                                  } catch (err) {
                                    console.error('Failed to add contribution:', err);
                                  }
                                }}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                onClick={() => {
                                  setIsAddingContribution(false);
                                  setNewContributionAmount('');
                                  setNewContributionNote('');
                                  setSelectedAccountId(null);
                                }}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                      ) : (
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => setIsAddingContribution(true)}
                          sx={{ mt: 1 }}
                        >
                          Add Money
                        </Button>
                      )}

                    {/* Contribution History */}
                    {contributions.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          History
                        </Typography>
                        <Stack spacing={1}>
                          {contributions.map((c) => {
                            const isEditing = editingContributionId === c.id;
                            const date = new Date(c.occurredAt);
                            return (
                              <Box
                                key={c.id}
                                sx={{
                                  p: 1.5, borderRadius: 1.5,
                                  bgcolor: isEditing ? 'grey.50' : 'background.paper',
                                  border: '1px solid', borderColor: isEditing ? 'primary.main' : 'divider',
                                  '&:hover .contrib-actions': { opacity: 1 },
                                }}
                              >
                                {isEditing ? (
                                  <Stack spacing={1}>
                                    <Stack direction="row" spacing={1}>
                                      <TextField label="Amount ($)" type="number" size="small" value={editContribAmount}
                                        onChange={(e) => setEditContribAmount(e.target.value)}
                                        inputProps={{ step: '0.01', min: '0.01' }} sx={{ flex: 1 }} />
                                      <TextField label="Date" type="date" size="small" value={editContribDate}
                                        onChange={(e) => setEditContribDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                    </Stack>
                                    <TextField label="Note" size="small" fullWidth value={editContribNote}
                                      onChange={(e) => setEditContribNote(e.target.value)} />
                                    {accounts.length > 0 && (
                                      <TextField select label="Funding Source" size="small" fullWidth
                                        value={editContribAccountId ?? ""}
                                        onChange={(e) => setEditContribAccountId(e.target.value || null)}>
                                        <MenuItem value=""><em>Not specified</em></MenuItem>
                                        {accounts.map((acc) => (
                                          <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                                        ))}
                                      </TextField>
                                    )}
                                    <Stack direction="row" spacing={1}>
                                      <Button size="small" variant="contained" onClick={async () => {
                                        if (!jar) return;
                                        try {
                                          const { jarBalance } = await api.updateJarContribution(jar.id, c.id, {
                                            amountCents: Math.round(parseFloat(editContribAmount) * 100),
                                            note: editContribNote || null,
                                            occurredAt: editContribDate,
                                            accountId: editContribAccountId,
                                          });
                                          setContributions(prev => prev.map(x => x.id === c.id ? {
                                            ...x,
                                            amountCents: Math.round(parseFloat(editContribAmount) * 100),
                                            note: editContribNote || null,
                                            occurredAt: editContribDate,
                                            accountId: editContribAccountId,
                                            accountName: accounts.find(a => a.id === editContribAccountId)?.name ?? null,
                                          } : x));
                                          onJarUpdated?.({ ...jar, currentAmountCents: jarBalance });
                                          setEditingContributionId(null);
                                        } catch (err) { console.error('Failed to update contribution:', err); }
                                      }}>Save</Button>
                                      <Button size="small" onClick={() => setEditingContributionId(null)}>Cancel</Button>
                                      <Box sx={{ flex: 1 }} />
                                      <Button size="small" color="error" onClick={async () => {
                                        if (!jar) return;
                                        try {
                                          const { jarBalance } = await api.deleteJarContribution(jar.id, c.id);
                                          setContributions(prev => prev.filter(x => x.id !== c.id));
                                          onJarUpdated?.({ ...jar, currentAmountCents: jarBalance });
                                          setEditingContributionId(null);
                                        } catch (err) { console.error('Failed to delete contribution:', err); }
                                      }}>Delete</Button>
                                    </Stack>
                                  </Stack>
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" fontWeight={600}>
                                          {c.userName}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} color="success.main">
                                          +{formatCurrency(c.amountCents / 100)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {date.toLocaleDateString()}{c.note ? ` · ${c.note}` : ''}
                                        </Typography>
                                        {c.accountName && (
                                          <Typography variant="caption" color="text.secondary">
                                            📍 {c.accountName}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                    <IconButton size="small" className="contrib-actions"
                                      sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                                      onClick={() => {
                                        setEditingContributionId(c.id);
                                        setEditContribAmount((c.amountCents / 100).toFixed(2));
                                        setEditContribNote(c.note ?? '');
                                        setEditContribDate(c.occurredAt?.split('T')[0] ?? '');
                                        setEditContribAccountId(c.accountId);
                                      }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                    {/* <Stack spacing={2}>
                      {participants.map((participant) => (
                        <Box
                          key={participant.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1.5,
                            bgcolor: "white",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "grey.200",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Avatar
                              sx={{
                                bgcolor: "grey.200",
                                color: "text.primary",
                                width: 40,
                                height: 40,
                              }}
                            >
                              {participant.name.charAt(0)}
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {participant.name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={500}>
                            $0.00
                          </Typography>
                        </Box>
                      ))}
                    </Stack> */}
                  </Box>
                </Stack>
              </Box>
            )}

            {/* Planning Tab */}
            {activeTab === 1 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Planned
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {formatCurrency(getPlanningTotal())}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                  >
                    Add Item
                  </Button>
                </Box>
                {planningItems.length > 0 ? (
                  <Stack spacing={2}>
                    {planningItems.map((item) => (
                      <Box key={item.id}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1.5,
                            bgcolor: "white",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.200",
                            "&:hover .delete-action": { opacity: 1 },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                            <TextField
                              value={item.name}
                              onChange={(e) =>
                                updateItemField(item.id, "name", e.target.value)
                              }
                              variant="standard"
                              size="small"
                              sx={{ minWidth: 100 }}
                            />
                            {(item.children ?? []).length > 0 ? (
                              <Box
                                sx={{
                                  width: 100,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  borderRadius: 1,
                                  mx: 1.5,
                                  bgcolor: "grey.100",
                                }}
                              >
                                <Typography color="text.secondary" sx={{ mr: 0.5 }}>
                                  $
                                </Typography>
                                <Typography variant="body2" color="text.primary" sx={{ textAlign: "right", flex: 1 }}>
                                  {formatNumber(getItemTotal(item))}
                                </Typography>
                              </Box>
                            ) : (
                              <TextField
                                value={editingAmountId === item.id ? editingAmountValue : formatNumber(item.amountCents / 100)}
                                onFocus={() => {
                                  setEditingAmountId(item.id);
                                  setEditingAmountValue((item.amountCents / 100).toFixed(2));
                                }}
                                onBlur={() => {
                                  updateItemField(item.id, "amount", editingAmountValue || "0");
                                  setEditingAmountId(null);
                                  setEditingAmountValue("");
                                }}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "" || !Number.isNaN(Number(val))) setEditingAmountValue(val);
                                }}
                                variant="standard"
                                size="small"
                                type="text"
                                sx={{ width: 100, mx: 1.5 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">$</InputAdornment>
                                  ),
                                }}
                                inputProps={{ min: 0, step: "0.01" }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                            <Select
                              value={item.status}
                              onChange={(e) =>
                                updateItemField(
                                  item.id,
                                  "status",
                                  e.target.value as string
                                )
                              }
                              size="small"
                              variant="standard"
                              disableUnderline
                              sx={{ width: 120 }}
                            >
                              {statusOptions.map((status) => (
                                <MenuItem
                                  key={status}
                                  value={status}
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  {status}
                                </MenuItem>
                              ))}
                            </Select>
                            <IconButton
                              size="small"
                              onClick={() => handleAddSubItem(item.id)}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              className="delete-action"
                              onClick={() => handleDeleteItem(item.id)}
                              sx={{
                                opacity: 0,
                                transition: "opacity 0.2s ease",
                                color: "text.secondary",
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        {(item.children ?? []).length > 0 && (
                          <Stack spacing={1} sx={{ ml: 3, mt: 1 }}>
                            {(item.children ?? []).map((subItem) => (
                              <Box
                                key={subItem.id}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  p: 1.5,
                                  bgcolor: "grey.50",
                                  borderRadius: 1,
                                  borderLeft: "3px solid",
                                  borderColor: "grey.300",
                                  "&:hover .delete-action": { opacity: 1 },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    flex: 1,
                                  }}
                                >
                                  <TextField
                                    value={subItem.name}
                                    onChange={(e) =>
                                      updateSubItemField(
                                        item.id,
                                        subItem.id,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    variant="standard"
                                    size="small"
                                    sx={{ minWidth: 180 }}
                                  />
                                  <TextField
                                    value={editingSubAmountKey === `${item.id}-${subItem.id}` ? editingSubAmountValue : formatNumber(subItem.amountCents / 100)}
                                    onFocus={() => {
                                      const key = `${item.id}-${subItem.id}`;
                                      setEditingSubAmountKey(key);
                                      setEditingSubAmountValue((subItem.amountCents / 100).toFixed(2));
                                    }}
                                    onBlur={() => {
                                      updateSubItemField(item.id, subItem.id, "amount", editingSubAmountValue || "0");
                                      setEditingSubAmountKey(null);
                                      setEditingSubAmountValue("");
                                    }}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === "" || !Number.isNaN(Number(val))) setEditingSubAmountValue(val);
                                    }}
                                    variant="standard"
                                    size="small"
                                    type="text"
                                    sx={{ width: 120 }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                    }}
                                    inputProps={{ min: 0, step: "0.01" }}
                                  />
                                </Box>
                                <Select
                                  value={subItem.status}
                                  onChange={(e) =>
                                    updateSubItemField(
                                      item.id,
                                      subItem.id,
                                      "status",
                                      e.target.value as string
                                    )
                                  }
                                  size="small"
                                  variant="standard"
                                  disableUnderline
                                  sx={{ minWidth: 120 }}
                                >
                                  {statusOptions.map((status) => (
                                    <MenuItem
                                      key={status}
                                      value={status}
                                      sx={{ display: "flex", alignItems: "center" }}
                                    >
                                      {status}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <IconButton
                                  size="small"
                                  className="delete-action"
                                  onClick={() =>
                                    handleDeleteSubItem(item.id, subItem.id)
                                  }
                                  sx={{
                                    opacity: 0,
                                    transition: "opacity 0.2s ease",
                                    color: "text.secondary",
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No items yet. Add one to get started.
                  </Typography>
                )}
              </Box>
            )}

            {/* Expenses Tab */}
            {activeTab === 2 && (
              <Box>
                {expenses.length > 0 ? (
                  <Stack spacing={2}>
                    {/* Summary Section */}
                    <Box sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Total Expenses
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {formatCurrency(expenses.reduce((sum, exp) => sum + exp.amountCents / 100, 0))}
                        </Typography>
                      </Box>
                      <Stack spacing={0.75}>
                        {Array.from(
                          new Set(expenses.map((e) => e.paidByName))
                        ).map((paidByName) => {
                          const total = expenses
                            .filter((e) => e.paidByName === paidByName)
                            .reduce((sum, e) => sum + e.amountCents / 100, 0);
                          return (
                            <Box
                              key={paidByName}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {paidByName} paid:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {formatCurrency(total)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Stack>

                      {/* Per-person split totals (from real split data) */}
                      {(() => {
                        const splitTotals: Record<string, number> = {};
                        for (const exp of expenses) {
                          for (const split of exp.splits) {
                            splitTotals[split.userId] = (splitTotals[split.userId] ?? 0) + split.amountCents;
                          }
                        }
                        const participantMap = new Map(participants.map(p => [p.id, p.name]));
                        const entries = Object.entries(splitTotals).filter(([_, v]) => v > 0);
                        if (entries.length <= 1) return null;
                        return (
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                              Per Person (from splits)
                            </Typography>
                            <Stack spacing={0.5}>
                              {entries.map(([uid, cents]) => (
                                <Box key={uid} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {participantMap.get(uid) ?? 'Unknown'}:
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500}>
                                    {formatCurrency(cents / 100)}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        );
                      })()}

                      {/* Settlement: minimal transactions to settle all debts */}
                      {(() => {
                        // Net balance per person: positive = is owed money, negative = owes money
                        const balances: Record<string, number> = {};
                        const nameMap: Record<string, string> = {};
                        for (const exp of expenses) {
                          balances[exp.paidByUserId] = (balances[exp.paidByUserId] ?? 0) + exp.amountCents;
                          nameMap[exp.paidByUserId] = exp.paidByName;
                          for (const split of exp.splits) {
                            balances[split.userId] = (balances[split.userId] ?? 0) - split.amountCents;
                            if (split.name) nameMap[split.userId] = split.name;
                          }
                        }
                        const getName = (uid: string) => nameMap[uid] ?? 'Unknown';
                        // Greedy debt simplification
                        const debtors = Object.entries(balances)
                          .filter(([_, v]) => v < -1)
                          .map(([uid, v]) => ({ uid, rem: -v }));
                        const creditors = Object.entries(balances)
                          .filter(([_, v]) => v > 1)
                          .map(([uid, v]) => ({ uid, rem: v }));
                        if (debtors.length === 0) return null;
                        const txns: Array<{ from: string; to: string; amountCents: number }> = [];
                        let di = 0, ci = 0;
                        while (di < debtors.length && ci < creditors.length) {
                          const amount = Math.min(debtors[di].rem, creditors[ci].rem);
                          if (amount > 1) txns.push({ from: debtors[di].uid, to: creditors[ci].uid, amountCents: amount });
                          debtors[di].rem -= amount;
                          creditors[ci].rem -= amount;
                          if (debtors[di].rem < 2) di++;
                          if (creditors[ci].rem < 2) ci++;
                        }
                        if (txns.length === 0) return null;
                        return (
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                              Settlement
                            </Typography>
                            <Stack spacing={0.5}>
                              {txns.map((tx, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2">
                                    <Box component="span" sx={{ color: 'error.main', fontWeight: 500 }}>{getName(tx.from)}</Box>
                                    {' owes '}
                                    <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>{getName(tx.to)}</Box>
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    {formatCurrency(tx.amountCents / 100)}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        );
                      })()}
                    </Box>

                    {/* Bulk mode toolbar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={bulkMode}
                            onChange={(e) => {
                              setBulkMode(e.target.checked);
                              if (!e.target.checked) setBulkSelectedIds(new Set());
                            }}
                          />
                        }
                        label={<Typography variant="body2">Bulk select</Typography>}
                      />
                      {bulkMode && bulkSelectedIds.size > 0 && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {bulkSelectedIds.size} selected
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setBulkSplitDialogOpen(true)}
                          >
                            Re-split ({bulkSelectedIds.size})
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setBulkSelectedIds(new Set(expenses.map(e => e.id)))}
                          >
                            Select all
                          </Button>
                        </>
                      )}
                      <Box sx={{ flex: 1 }} />
                      {jarCategories.length > 0 && (
                        <Tooltip title="Auto-categorize uncategorized expenses using your personal transaction rules">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AutoFixHighIcon />}
                            onClick={async () => {
                              if (!jar) return;
                              try {
                                const result = await api.autoCategorizeJarExpenses(jar.id);
                                if (result.categorized > 0) {
                                  // Refresh expenses and categories
                                  const { expenses: refreshed } = await api.getJarExpenses(jar.id);
                                  setExpenses(refreshed);
                                  const { categories } = await api.getJarCategories(jar.id);
                                  setJarCategories(categories);
                                }
                              } catch (err) { console.error('Failed to auto-categorize:', err); }
                            }}
                          >
                            Auto-categorize
                          </Button>
                        </Tooltip>
                      )}
                    </Box>

                    {expenses.map((expense) => {
                      const { month, day } = formatExpenseDate(expense.occurredAt);
                      const isExpanded = expandedExpenseId === expense.id;
                      const expCategory = jarCategories.find(c => c.id === expense.categoryId);
                      return (
                        <Box
                          key={expense.id}
                          sx={{
                            bgcolor: "white",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: isExpanded ? "primary.main" : "grey.200",
                            overflow: "hidden",
                            transition: "border-color 0.2s",
                          }}
                        >
                          {/* Expense row (clickable) */}
                          <Box
                            onClick={() => !bulkMode && handleExpandExpense(expense)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              p: 1.5,
                              cursor: bulkMode ? "default" : "pointer",
                              '&:hover': !bulkMode ? { bgcolor: "grey.50" } : {},
                              '&:hover .delete-expense': { opacity: 1 },
                            }}
                          >
                            {bulkMode && (
                              <Checkbox
                                size="small"
                                checked={bulkSelectedIds.has(expense.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setBulkSelectedIds(prev => {
                                    const next = new Set(prev);
                                    if (next.has(expense.id)) next.delete(expense.id);
                                    else next.add(expense.id);
                                    return next;
                                  });
                                }}
                              />
                            )}
                            <Box sx={{ width: 44, textAlign: "center", color: "text.secondary", flexShrink: 0 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>{month}</Typography>
                              <Typography variant="h6" sx={{ lineHeight: 1 }}>{day}</Typography>
                            </Box>
                            <Box
                              sx={{
                                width: 40, height: 40, borderRadius: 2,
                                bgcolor: expense.iconBg,
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
                              }}
                            >
                              {expense.icon}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                                    {expense.description}
                                  </Typography>
                                  {expCategory && (
                                    <Chip label={expCategory.name} size="small" variant="outlined"
                                      sx={{ fontSize: '0.65rem', height: 20, maxWidth: 100 }} />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {expense.paidByName} paid {formatCurrency(expense.amountCents / 100)}
                                </Typography>
                              </Box>
                              {(() => {
                                if (!currentUser) return null;
                                const yourSplit = expense.splits.find(s => s.userId === currentUser.id);
                                const youPaid = expense.paidByUserId === currentUser.id;
                                if (youPaid && yourSplit) {
                                  const owedToYou = expense.amountCents - yourSplit.amountCents;
                                  if (owedToYou > 0) return (
                                    <Typography variant="caption" fontWeight={600} color="success.main" sx={{ textAlign: 'right', flexShrink: 0 }}>
                                      you are owed<br />{formatCurrency(owedToYou / 100)}
                                    </Typography>
                                  );
                                  return (
                                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', flexShrink: 0 }}>
                                      you paid<br />your share
                                    </Typography>
                                  );
                                }
                                if (youPaid && !yourSplit) return (
                                  <Typography variant="caption" fontWeight={600} color="success.main" sx={{ textAlign: 'right', flexShrink: 0 }}>
                                    others owe you<br />{formatCurrency(expense.amountCents / 100)}
                                  </Typography>
                                );
                                if (!youPaid && yourSplit) return (
                                  <Typography variant="caption" fontWeight={600} color="warning.main" sx={{ textAlign: 'right', flexShrink: 0 }}>
                                    you owe<br />{formatCurrency(yourSplit.amountCents / 100)}
                                  </Typography>
                                );
                                return (
                                  <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'right', flexShrink: 0 }}>
                                    not involved
                                  </Typography>
                                );
                              })()}
                            </Box>
                            <IconButton
                              size="small"
                              className="delete-expense"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await api.deleteJarExpense(jar.id, expense.id);
                                  setExpenses(prev => prev.filter(ex => ex.id !== expense.id));
                                  if (isExpanded) setExpandedExpenseId(null);
                                } catch (err) {
                                  console.error('Failed to delete expense:', err);
                                }
                              }}
                              sx={{ opacity: 0, transition: "opacity 0.2s ease", color: "text.secondary" }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          {/* Expanded split detail / edit panel */}
                          <Collapse in={isExpanded}>
                            <Box sx={{ px: 2, pb: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                              {/* Current split breakdown */}
                              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mt: 1.5, mb: 1, display: 'block' }}>
                                Split ({splitTypeToLabel[expense.splitType] ?? expense.splitType})
                              </Typography>

                              {/* Paid by selector */}
                              <FormControl size="small" sx={{ mb: 1.5, minWidth: 160 }}>
                                <TextField
                                  select
                                  label="Paid by"
                                  value={editPaidBy}
                                  onChange={(e) => setEditPaidBy(e.target.value)}
                                  size="small"
                                >
                                  {participants.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                  ))}
                                </TextField>
                              </FormControl>

                              {/* Split type toggle + participant inputs */}
                              <SplitEditor
                                participants={participants}
                                totalAmount={expense.amountCents / 100}
                                splitType={editSplitType}
                                onSplitTypeChange={(v) => handleEditSplitTypeChange(v, expense)}
                                selectedParticipants={editSelectedParticipants}
                                onToggleParticipant={(id) =>
                                  setEditSelectedParticipants(prev => {
                                    const s = new Set(prev);
                                    s.has(id) ? s.delete(id) : s.add(id);
                                    return s;
                                  })
                                }
                                onSelectAll={() =>
                                  setEditSelectedParticipants(new Set(participants.map(p => p.id)))
                                }
                                splitAllocations={editSplitAmounts}
                                onUpdateAllocation={(id, val) =>
                                  setEditSplitAmounts(prev => ({ ...prev, [id]: val }))
                                }
                                splitPercentages={editSplitPercentages}
                                onUpdatePercentage={(id, val) =>
                                  setEditSplitPercentages(prev => ({ ...prev, [id]: val }))
                                }
                                splitShares={editSplitShares}
                                onUpdateShares={(id, val) =>
                                  setEditSplitShares(prev => ({ ...prev, [id]: val }))
                                }
                                adjustmentAmounts={editAdjustmentAmounts}
                                onUpdateAdjustment={(id, val) =>
                                  setEditAdjustmentAmounts(prev => ({ ...prev, [id]: val }))
                                }
                                validationError={getEditValidationError(expense.amountCents)}
                              />

                              {/* Expense category editor */}
                              <FormControl size="small" sx={{ mb: 1.5, minWidth: 160 }}>
                                <TextField
                                  select
                                  label="Category"
                                  value={expense.categoryId ?? ""}
                                  onChange={async (e) => {
                                    const catId = e.target.value || null;
                                    try {
                                      await api.updateJarExpense(jar.id, expense.id, { categoryId: catId });
                                      setExpenses(prev => prev.map(ex =>
                                        ex.id === expense.id ? { ...ex, categoryId: catId } : ex
                                      ));
                                    } catch (err) { console.error('Failed to update category:', err); }
                                  }}
                                  size="small"
                                >
                                  <MenuItem value=""><em>No category</em></MenuItem>
                                  {jarCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>{cat.icon} {cat.name}</MenuItem>
                                  ))}
                                </TextField>
                              </FormControl>

                              {/* Save / Cancel buttons */}
                              <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={savingSplits || !!getEditValidationError(expense.amountCents)}
                                  onClick={() => handleSaveExpenseSplits(expense)}
                                >
                                  {savingSplits ? 'Saving…' : 'Save'}
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => setExpandedExpenseId(null)}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setIsAddingExpense(true)}
                    >
                      Add Expense
                    </Button>
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No expenses recorded yet
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setIsAddingExpense(true)}
                    >
                      Add Expense
                    </Button>
                  </Box>
                )}

                {/* Bulk Split Dialog */}
                <Dialog open={bulkSplitDialogOpen} onClose={() => setBulkSplitDialogOpen(false)} maxWidth="xs" fullWidth>
                  <DialogTitle>Re-split {bulkSelectedIds.size} expenses</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        This will update the split method for all selected expenses. Each expense will be re-divided among all jar members using the chosen method.
                      </Typography>
                      <FormControl fullWidth size="small">
                        <TextField
                          select
                          label="Split Method"
                          value={bulkSplitType}
                          onChange={(e) => setBulkSplitType(e.target.value)}
                          size="small"
                        >
                          <MenuItem value="equally">Equally</MenuItem>
                          <MenuItem value="percent">By Percent</MenuItem>
                          <MenuItem value="shares">By Shares</MenuItem>
                        </TextField>
                      </FormControl>
                      {bulkSplitType === 'percent' && (
                        <Stack spacing={1}>
                          {participants.map(p => (
                            <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ flex: 1 }}>{p.name}</Typography>
                              <TextField
                                type="number" size="small" sx={{ width: 90 }}
                                defaultValue={Math.round(100 / participants.length)}
                                inputProps={{ min: 0, max: 100 }}
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                id={`bulk-pct-${p.id}`}
                              />
                            </Box>
                          ))}
                        </Stack>
                      )}
                      {bulkSplitType === 'shares' && (
                        <Stack spacing={1}>
                          {participants.map(p => (
                            <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ flex: 1 }}>{p.name}</Typography>
                              <TextField
                                type="number" size="small" sx={{ width: 80 }}
                                defaultValue={1} inputProps={{ min: 0 }}
                                id={`bulk-shares-${p.id}`}
                              />
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setBulkSplitDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={async () => {
                      if (!jar) return;
                      let perUser: Record<string, number> | undefined;
                      if (bulkSplitType === 'percent') {
                        perUser = {};
                        for (const p of participants) {
                          const el = document.getElementById(`bulk-pct-${p.id}`) as HTMLInputElement | null;
                          perUser[p.id] = parseFloat(el?.value ?? '0');
                        }
                      } else if (bulkSplitType === 'shares') {
                        perUser = {};
                        for (const p of participants) {
                          const el = document.getElementById(`bulk-shares-${p.id}`) as HTMLInputElement | null;
                          perUser[p.id] = parseFloat(el?.value ?? '1');
                        }
                      }
                      try {
                        await api.bulkSplitJarExpenses(jar.id, {
                          expenseIds: Array.from(bulkSelectedIds),
                          splitType: bulkSplitType,
                          perUser,
                        });
                        // Refresh expenses
                        const { expenses: refreshed } = await api.getJarExpenses(jar.id);
                        setExpenses(refreshed);
                        setBulkSelectedIds(new Set());
                        setBulkMode(false);
                        setBulkSplitDialogOpen(false);
                      } catch (err) { console.error('Bulk split failed:', err); }
                    }}>
                      Apply
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}

            {/* Members Tab */}
            {activeTab === 3 && (
              <Box>
                <Stack spacing={3}>
                  {/* Member count header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {jar.members.length} {jar.members.length === 1 ? "Member" : "Members"}
                    </Typography>
                    {isOwner && (
                      <Chip
                        label="Owner"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {/* Feedback alerts */}
                  {memberError && (
                    <Alert severity="error" onClose={() => setMemberError(null)}>
                      {memberError}
                    </Alert>
                  )}
                  {memberSuccess && (
                    <Alert severity="success" onClose={() => setMemberSuccess(null)}>
                      {memberSuccess}
                    </Alert>
                  )}

                  {/* Invite new member (owner only) */}
                  {isOwner && (
                    <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                        Invite a member
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                          label="Email address"
                          type="email"
                          size="small"
                          fullWidth
                          value={inviteEmail}
                          onChange={(e) => {
                            setInviteEmail(e.target.value);
                            setMemberError(null);
                          }}
                          placeholder="friend@example.com"
                          disabled={inviting}
                        />
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                          <Select
                            value={inviteRole}
                            onChange={(e) => setInviteRole(e.target.value as "VIEWER" | "EDITOR")}
                            disabled={inviting}
                          >
                            <MenuItem value="VIEWER">Viewer</MenuItem>
                            <MenuItem value="EDITOR">Editor</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={inviting || !inviteEmail.trim()}
                          onClick={async () => {
                            setInviting(true);
                            setMemberError(null);
                            setMemberSuccess(null);
                            try {
                              const { invite } = await api.createJarInvite(jar.id, {
                                email: inviteEmail.trim(),
                                role: inviteRole,
                              });
                              setInviteEmail("");
                              setMemberSuccess(`Invite sent to ${invite.invitedEmail} as ${invite.role.toLowerCase()}`);
                            } catch (err: any) {
                              setMemberError(err.message || "Failed to invite member");
                            } finally {
                              setInviting(false);
                            }
                          }}
                          sx={{ whiteSpace: "nowrap", minWidth: 80, height: 40 }}
                          startIcon={<PersonAddIcon />}
                        >
                          {inviting ? "…" : "Invite"}
                        </Button>
                      </Stack>
                    </Box>
                  )}

                  {/* Member list */}
                  <Stack spacing={1}>
                    {jar.members.map((member) => {
                      const isCurrentUser = currentUser?.id === member.userId;
                      const isMemberOwner = member.role === "OWNER";
                      const displayName = member.user.name || member.user.email || "Unknown";
                      const initials = displayName
                        .split(" ")
                        .map((w) => w[0]?.toUpperCase() ?? "")
                        .join("")
                        .slice(0, 2);
                      return (
                        <Box
                          key={member.userId}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              fontSize: 14,
                              bgcolor: isMemberOwner ? "primary.main" : "grey.400",
                            }}
                          >
                            {initials}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {displayName}{isCurrentUser ? " (you)" : ""}
                            </Typography>
                            {member.user.email && member.user.name && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {member.user.email}
                              </Typography>
                            )}
                          </Box>

                          {/* Role chip / selector */}
                          {isMemberOwner ? (
                            <Chip label="Owner" size="small" color="primary" />
                          ) : isOwner ? (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value={member.role}
                                disabled={updatingRoleId === member.userId}
                                onChange={async (e) => {
                                  const newRole = e.target.value as string;
                                  setUpdatingRoleId(member.userId);
                                  try {
                                    await api.addJarMember(jar.id, {
                                      userId: member.userId,
                                      role: newRole,
                                    });
                                    const updatedMembers = jar.members.map((m) =>
                                      m.userId === member.userId ? { ...m, role: newRole } : m
                                    );
                                    onJarUpdated?.({ ...jar, members: updatedMembers });
                                  } catch (err: any) {
                                    setMemberError(err.message || "Failed to update role");
                                  } finally {
                                    setUpdatingRoleId(null);
                                  }
                                }}
                                sx={{ "& .MuiSelect-select": { py: 0.5, fontSize: "0.8rem" } }}
                              >
                                <MenuItem value="VIEWER">Viewer</MenuItem>
                                <MenuItem value="EDITOR">Editor</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              label={member.role === "EDITOR" ? "Editor" : "Viewer"}
                              size="small"
                              variant="outlined"
                            />
                          )}

                          {/* Remove button (owner can remove others, non-owners can leave) */}
                          {(isOwner && !isMemberOwner) || (!isOwner && isCurrentUser && !isMemberOwner) ? (
                            <Tooltip title={isCurrentUser ? "Leave jar" : "Remove member"}>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={removingMemberId === member.userId}
                                onClick={async () => {
                                  setRemovingMemberId(member.userId);
                                  setMemberError(null);
                                  try {
                                    await api.removeJarMember(jar.id, member.userId);
                                    const updatedMembers = jar.members.filter((m) => m.userId !== member.userId);
                                    onJarUpdated?.({ ...jar, members: updatedMembers });
                                    setMemberSuccess(`${displayName} removed`);
                                  } catch (err: any) {
                                    setMemberError(err.message || "Failed to remove member");
                                  } finally {
                                    setRemovingMemberId(null);
                                  }
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>

                  {/* Role explanation */}
                  <Box sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" component="div">
                      <strong>Owner</strong> — Full control including managing members
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      <strong>Editor</strong> — Can add expenses, contributions, and edit jar details
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      <strong>Viewer</strong> — Can view the jar but cannot make changes
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
          </Box>
          </Stack>
        )}
      </DialogContent>
      {!isAddingExpense && (
          <DialogActions>
          <Button variant="contained" onClick={onClose}>Close</Button>
          {/* <Button variant="contained" onClick={() => onAddMoney?.(jar)}>
            Add Money
          </Button> */}
        </DialogActions>
      )}
    </Dialog>
  );
}
